import type { Identifier } from "ra-core";

import type { GulaPayment, GulaSubscription } from "../gulaTypes";
import { RECURRING_KINDS } from "./profit";

/**
 * Money dashboard figures from the Stripe payments and subscriptions synced
 * into the CRM. Amounts are what clients paid (VAT included). Dates are read
 * in the browser's time zone (the team is in Spain).
 */

export const REVENUE_KINDS = [
  "equipment",
  "rental",
  "maintenance",
  "installments",
  "other",
] as const;
export type RevenueKind = (typeof REVENUE_KINDS)[number];

export type MonthBucket = Record<RevenueKind, number> & {
  key: string;
  label: string;
  total: number;
};

export type ClientValue = { contactId: Identifier; total: number };

export type MoneyStats = {
  today: number;
  todayCount: number;
  week: number;
  month: number;
  monthCount: number;
  previousMonthToDate: number;
  monthChangePct: number | null;
  year: number;
  allTime: number;
  payingClients: number;
  averageClientValue: number;
  mrr: number;
  mrrByKind: { rental: number; maintenance: number };
  activeRecurring: number;
  installmentsPending: number;
  atRisk: GulaSubscription[];
  months: MonthBucket[];
  topClients: ClientValue[];
  recent: GulaPayment[];
};

const LIVE_STATUSES = ["active", "trialing"];
const AT_RISK_STATUSES = ["past_due", "unpaid", "incomplete"];

const round2 = (value: number) => Math.round(value * 100) / 100;
const amountOf = (payment: GulaPayment) => Number(payment.amount) || 0;
const kindOf = (kind: string): RevenueKind =>
  (REVENUE_KINDS as readonly string[]).includes(kind)
    ? (kind as RevenueKind)
    : "other";
const monthKey = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
const sumBetween = (payments: GulaPayment[], from: Date, to: Date) =>
  payments
    .filter((payment) => {
      const paidAt = new Date(payment.paid_at as string);
      return paidAt >= from && paidAt < to;
    })
    .reduce((sum, payment) => sum + amountOf(payment), 0);

export const monthlyAmount = (subscription: GulaSubscription) =>
  subscription.billing_interval === "year"
    ? (Number(subscription.amount) || 0) / 12
    : Number(subscription.amount) || 0;

export const buildMoneyStats = ({
  payments,
  subscriptions,
  now,
}: {
  payments: GulaPayment[];
  subscriptions: GulaSubscription[];
  now: Date;
}): MoneyStats => {
  const paid = payments.filter(
    (payment) => payment.status === "paid" && !!payment.paid_at,
  );

  const startOfToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
  );
  const tomorrow = new Date(startOfToday.getTime() + 864e5);
  const startOfWeek = new Date(startOfToday);
  startOfWeek.setDate(
    startOfToday.getDate() - ((startOfToday.getDay() + 6) % 7),
  );
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const startOfPreviousMonth = new Date(
    now.getFullYear(),
    now.getMonth() - 1,
    1,
  );
  // Same days of the previous month, for a fair comparison
  const previousMonthDays = new Date(
    now.getFullYear(),
    now.getMonth(),
    0,
  ).getDate();
  const previousMonthCut = new Date(
    startOfPreviousMonth.getFullYear(),
    startOfPreviousMonth.getMonth(),
    Math.min(now.getDate(), previousMonthDays) + 1,
  );
  const startOfYear = new Date(now.getFullYear(), 0, 1);

  const todayPayments = paid.filter((payment) => {
    const paidAt = new Date(payment.paid_at as string);
    return paidAt >= startOfToday && paidAt < tomorrow;
  });
  const monthPayments = paid.filter(
    (payment) => new Date(payment.paid_at as string) >= startOfMonth,
  );
  const month = sumBetween(paid, startOfMonth, tomorrow);
  const previousMonthToDate = sumBetween(
    paid,
    startOfPreviousMonth,
    previousMonthCut,
  );

  const months: MonthBucket[] = [];
  for (let offset = 11; offset >= 0; offset -= 1) {
    const date = new Date(now.getFullYear(), now.getMonth() - offset, 1);
    const label = date.toLocaleDateString("es-ES", { month: "short" });
    months.push({
      key: monthKey(date),
      label: label.replace(".", ""),
      total: 0,
      equipment: 0,
      rental: 0,
      maintenance: 0,
      installments: 0,
      other: 0,
    });
  }
  const bucketByKey = new Map(months.map((bucket) => [bucket.key, bucket]));
  paid.forEach((payment) => {
    const bucket = bucketByKey.get(
      monthKey(new Date(payment.paid_at as string)),
    );
    if (!bucket) return;
    const kind = kindOf(payment.kind);
    bucket[kind] = round2(bucket[kind] + amountOf(payment));
    bucket.total = round2(bucket.total + amountOf(payment));
  });

  const byClient = new Map<string, ClientValue>();
  paid.forEach((payment) => {
    if (payment.contact_id == null) return;
    const key = String(payment.contact_id);
    const current = byClient.get(key) ?? {
      contactId: payment.contact_id,
      total: 0,
    };
    byClient.set(key, { ...current, total: current.total + amountOf(payment) });
  });
  const clientValues = [...byClient.values()].map((client) => ({
    ...client,
    total: round2(client.total),
  }));
  const allTime = paid.reduce((sum, payment) => sum + amountOf(payment), 0);

  const live = subscriptions.filter((subscription) =>
    LIVE_STATUSES.includes(subscription.status),
  );
  const recurringLive = live.filter((subscription) =>
    RECURRING_KINDS.includes(subscription.kind),
  );
  const mrrOf = (kind: string) =>
    round2(
      recurringLive
        .filter((subscription) => subscription.kind === kind)
        .reduce((sum, subscription) => sum + monthlyAmount(subscription), 0),
    );
  const mrrByKind = {
    rental: mrrOf("rental"),
    maintenance: mrrOf("maintenance"),
  };

  return {
    today: round2(todayPayments.reduce((sum, p) => sum + amountOf(p), 0)),
    todayCount: todayPayments.length,
    week: round2(sumBetween(paid, startOfWeek, tomorrow)),
    month: round2(month),
    monthCount: monthPayments.length,
    previousMonthToDate: round2(previousMonthToDate),
    monthChangePct:
      previousMonthToDate > 0
        ? Math.round(
            ((month - previousMonthToDate) / previousMonthToDate) * 100,
          )
        : null,
    year: round2(sumBetween(paid, startOfYear, tomorrow)),
    allTime: round2(allTime),
    payingClients: clientValues.length,
    averageClientValue: clientValues.length
      ? round2(allTime / clientValues.length)
      : 0,
    mrr: round2(mrrByKind.rental + mrrByKind.maintenance),
    mrrByKind,
    activeRecurring: recurringLive.length,
    installmentsPending: live.filter(
      (subscription) => subscription.kind === "installments",
    ).length,
    atRisk: subscriptions.filter((subscription) =>
      AT_RISK_STATUSES.includes(subscription.status),
    ),
    months,
    topClients: clientValues.sort((a, b) => b.total - a.total).slice(0, 5),
    recent: [...paid]
      .sort((a, b) => (b.paid_at as string).localeCompare(a.paid_at as string))
      .slice(0, 8),
  };
};
